import React, {CSSProperties, useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faChevronLeft,
    faChevronRight,
    faForwardStep,
    faBackwardStep,
    faPause,
    faPlay
} from "@fortawesome/free-solid-svg-icons";
import {DateTime} from 'luxon';
import {BASE_MONTHS} from "../helpers/months";
import './map-timeline-control.scss';

export type MapTimelineControlProps = {
    paused: boolean,
    currentMarker: any,
    markers: any,
    year?: string|number,
    month?: string|number,
    togglePaused: () => void,
    rewind: () => void,
    fastForward: () => void,
    toStart: () => void,
    toEnd: () => void,
    onShowTimePeriodSelector: () => void
}

export function MapTimelineControl({
    paused,
    currentMarker,
    markers,
    year,
    month,
    togglePaused,
    rewind,
    fastForward,
    toStart,
    toEnd,
    onShowTimePeriodSelector
}: MapTimelineControlProps) {
    const isLifeList = year === 'life';
    const currentDate = useMemo(() => {
        if (!currentMarker?.properties?.date) return undefined;

        const date = DateTime.fromISO(currentMarker.properties.date);
        return date.isValid ? date : undefined;
    }, [currentMarker]);
    const markerDateRange = useMemo(() => {
        const markerDates = (markers?.features ?? [])
            .map((feature: any) => feature?.properties?.date ? DateTime.fromISO(feature.properties.date) : undefined)
            .filter((date: DateTime|undefined): date is DateTime => Boolean(date?.isValid));

        if (!markerDates.length) {
            const today = DateTime.local();
            return {
                firstDate: today.startOf('year'),
                lastDate: today.endOf('day')
            };
        }

        const firstDate = markerDates.reduce((earliest: DateTime, date: DateTime) => date < earliest ? date : earliest, markerDates[0]);

        return {
            firstDate,
            lastDate: DateTime.local().endOf('day')
        };
    }, [markers]);

    const parsedMonth = useMemo(() => {
        return month && month !== 'null' ? parseInt(month as string) : undefined;
    }, [month]);

    const selectedMonthLabel = useMemo(() => {
        return parsedMonth ? BASE_MONTHS.find(baseMonth => baseMonth.value === parsedMonth)?.label : undefined;
    }, [parsedMonth]);

    const periodRange = useMemo(() => {
        if (isLifeList) {
            return {
                periodStart: markerDateRange.firstDate.startOf('year'),
                periodEnd: markerDateRange.lastDate
            };
        }

        const parsedYear = year && year !== 'life' ? parseInt(year as string) : currentDate?.year ?? DateTime.local().year;
        const periodStart = parsedMonth
            ? DateTime.local(parsedYear, parsedMonth, 1).startOf('day')
            : DateTime.local(parsedYear, 1, 1).startOf('day');
        const periodEnd = parsedMonth
            ? periodStart.endOf('month')
            : DateTime.local(parsedYear, 12, 31).endOf('day');

        return {periodStart, periodEnd};
    }, [currentDate, isLifeList, markerDateRange, parsedMonth, year]);

    const getDateProgressPercent = (date: DateTime) => {
        const totalDuration = periodRange.periodEnd.toMillis() - periodRange.periodStart.toMillis();
        if (totalDuration <= 0) return 0;

        const elapsed = date.toMillis() - periodRange.periodStart.toMillis();
        return Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));
    }

    const progressPercent = useMemo(() => {
        if (!currentDate) return 0;

        return getDateProgressPercent(currentDate);
        // getDateProgressPercent is derived from periodRange and intentionally scoped to this render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, parsedMonth, year]);

    const dailySightings = useMemo(() => {
        const countsByDate = new Map<string, number>();

        for (const feature of markers?.features ?? []) {
            const date = feature?.properties?.date ? DateTime.fromISO(feature.properties.date) : undefined;
            if (!date?.isValid) continue;

            if (date < periodRange.periodStart || date > periodRange.periodEnd) continue;

            const key = date.toISODate();
            countsByDate.set(key, (countsByDate.get(key) ?? 0) + 1);
        }

        const maxCount = Math.max(...Array.from(countsByDate.values()), 1);

        return Array.from(countsByDate.entries()).map(([dateKey, count]) => {
            const date = DateTime.fromISO(dateKey);

            return {
                dateKey,
                count,
                progress: getDateProgressPercent(date),
                height: Math.max(18, (count / maxCount) * 100)
            };
        });
        // getDateProgressPercent is derived from periodRange and intentionally scoped to this render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markers, periodRange]);

    const activeDateLabel = useMemo(() => {
        return currentDate ? currentDate.toFormat('LLL d, y') : undefined;
    }, [currentDate]);

    const timelineLabels = useMemo(() => {
        if (isLifeList) {
            const startYear = periodRange.periodStart.year;
            const endYear = periodRange.periodEnd.year;
            const yearCount = Math.max(endYear - startYear, 1);
            const interval = Math.max(1, Math.ceil(yearCount / 8));
            const labels = [];

            for (let labelYear = startYear; labelYear <= endYear; labelYear += interval) {
                labels.push({
                    label: labelYear.toString(),
                    value: yearCount === 0 ? 0 : ((labelYear - startYear) / yearCount) * 100
                });
            }

            if (labels[labels.length - 1]?.label !== endYear.toString()) {
                labels.push({
                    label: endYear.toString(),
                    value: 100
                });
            }

            return labels;
        }

        if (!parsedMonth) {
            return BASE_MONTHS.map(baseMonth => ({
                label: baseMonth.label.slice(0, 3),
                value: ((baseMonth.value - 1) / 11) * 100
            }));
        }

        const parsedYear = year && year !== 'life' ? parseInt(year as string) : currentDate?.year ?? DateTime.local().year;
        const daysInMonth = Number(DateTime.local(parsedYear, parsedMonth, 1).daysInMonth ?? 31);
        const labelDays = [1, Math.max(1, Math.round(daysInMonth / 2)), daysInMonth];

        return labelDays.map(day => ({
            label: `${selectedMonthLabel?.slice(0, 3) ?? ''} ${day}`,
            value: daysInMonth === 1 ? 0 : ((day - 1) / (daysInMonth - 1)) * 100
        }));
    }, [currentDate, isLifeList, parsedMonth, periodRange, selectedMonthLabel, year]);

    const periodLabel = useMemo(() => {
        if (year === 'life') return 'Your Life List';
        return [selectedMonthLabel, year].filter(Boolean).join(' ') || 'Life List';
    }, [selectedMonthLabel, year]);

    const timelineStyle = {
        '--map-timeline-progress': `${progressPercent}%`
    } as CSSProperties;

    return (
        <section className="map-timeline" aria-label="Map timeline controls">
            <div className="map-timeline__period">
                <div className="map-timeline__period-heading">Exploring {periodLabel}</div>
                <button type="button" className="map-timeline__period-btn" onClick={onShowTimePeriodSelector}>
                    <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                    <span>Change Dates</span>
                </button>
                {activeDateLabel ?
                    <div className="map-timeline__mobile-date">{activeDateLabel}</div>
                    : null
                }
            </div>
            <div className="map-timeline__controls">
                <button type="button" className="map-timeline__skip-btn" onClick={toStart} aria-label="Jump to beginning">
                    <FontAwesomeIcon icon={faBackwardStep} aria-hidden="true" />
                </button>
                <button type="button" className="map-timeline__step-btn" onClick={rewind} aria-label="Previous species">
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="map-timeline__play-btn"
                    onClick={togglePaused}
                    aria-label={paused ? 'Play timeline' : 'Pause timeline'}
                >
                    <FontAwesomeIcon icon={paused ? faPlay : faPause} aria-hidden="true" />
                </button>
                <div className="map-timeline__track" style={timelineStyle} aria-hidden="true">
                    <div className="map-timeline__bars">
                        {dailySightings.map(day => (
                            <span
                                key={day.dateKey}
                                className={`map-timeline__bar ${day.progress <= progressPercent ? 'is-past' : ''}`}
                                style={{
                                    '--map-timeline-bar-position': `${day.progress}%`,
                                    '--map-timeline-bar-height': `${day.height}%`
                                } as CSSProperties}
                            />
                        ))}
                    </div>
                    {activeDateLabel ?
                        <span className="map-timeline__active-marker">
                            <span className="map-timeline__date-flag">{activeDateLabel}</span>
                            <span className="map-timeline__active-dot" />
                        </span>
                        : null
                    }
                    {timelineLabels.map(label => (
                        <span
                            key={label.label}
                            className="map-timeline__tick"
                            style={{'--map-timeline-tick-position': `${label.value}%`} as CSSProperties}
                        >
                            <span className="map-timeline__dot" />
                            <span className="map-timeline__month">{label.label}</span>
                        </span>
                    ))}
                </div>
                <button type="button" className="map-timeline__step-btn" onClick={fastForward} aria-label="Next species">
                    <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                </button>
                <button type="button" className="map-timeline__skip-btn" onClick={toEnd} aria-label="Jump to end">
                    <FontAwesomeIcon icon={faForwardStep} aria-hidden="true" />
                </button>
            </div>
        </section>
    )
}
